import styled from 'styled-components';
import Image from 'next/image';
import {Club} from '@src/models/club';
import {categoryService} from '@src/features/auth/logic/category-service';
import {ApiImage} from '@src/common/ui/ApiImage';
import {useTranslation} from 'next-i18next';
import {useState, useEffect} from 'react';
import {handleGoogleMapRedirect} from '@src/utils/handleGoogleMapRedirect';
import {useRouter} from 'next/router';
import {chip, interactiveCard} from '@src/common/ui/cardStyles';
import {MenuRowChevron} from '@src/common/ui/MenuCard';

const ChevronWrapper = styled.div`
  display: flex;
  align-self: center;
`;

const ClubCardWrapper = styled.div`
  ${interactiveCard};
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0;
  padding: 10px 12px;
  border-radius: 14px;
`;

const Avatar = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  overflow: hidden;
  background: ${(p) => p.theme.colors.primary100};

  img {
    width: 48px !important;
    height: 48px !important;
    object-fit: cover;
    border-radius: 14px;
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Title = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: ${(p) => p.theme.colors.primary500};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CategoryChip = styled.span`
  ${chip};
  align-self: flex-start;
  max-width: 100%;
  padding: 2px 8px;
  background: ${(p) => p.theme.colors.secondary100};
  color: ${(p) => p.theme.colors.violet600};
`;

const CategoryChipLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaItem = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  color: ${(p) => p.theme.colors.textMuted};
  font-family: inherit;

  &:disabled {
    cursor: default;
  }
`;

interface ClubCardProps {
  club: Club;
}

export const ClubCard = ({club}: ClubCardProps) => {
  const {t} = useTranslation();
  const router = useRouter();
  const [token, setToken] = useState('');
  useEffect(() => {
    const tmpToken = localStorage.getItem('jwt');
    if (tmpToken) {
      setToken(tmpToken);
    }
  }, []);

  const logo = club.files?.find((file) => file.type === 'COMPANY_AVATAR');
  const categoryLabel = categoryService.getMostDetailedCategory(
    club.categories ?? []
  );
  const cityName = club.address?.city;
  const city = cityName || '-';

  return (
    <ClubCardWrapper
      onClick={() => {
        const url = token ? `/clubs/${club.id}` : `/clubs/${club.id}/guest`;
        router.push(url);
      }}
    >
      <Avatar>
        {logo ? (
          <ApiImage
            type="THUMBNAIL"
            imageId={logo.id}
            width={48}
            height={48}
            style={{borderRadius: 14, objectFit: 'cover'}}
          />
        ) : (
          <Image
            alt=""
            src="/images/mock-logo.png"
            width={48}
            height={48}
            style={{borderRadius: 14}}
          />
        )}
      </Avatar>
      <Body>
        <TitleRow>
          <Title>{club.companyName ?? ''}</Title>
        </TitleRow>
        {categoryLabel && categoryLabel !== '-' ? (
          <CategoryChip>
            <CategoryChipLabel>{categoryLabel}</CategoryChipLabel>
          </CategoryChip>
        ) : null}
        <Meta>
          <MetaItem
            type="button"
            disabled={!cityName}
            onClick={(e) => {
              e.stopPropagation();
              if (cityName) {
                handleGoogleMapRedirect(cityName);
              }
            }}
          >
            <Image
              alt=""
              src="/icons/location.svg"
              width={12}
              height={12}
            />
            {city}
          </MetaItem>
          <MetaItem type="button" disabled>
            <Image
              alt=""
              src="/icons/people-purple.svg"
              width={12}
              height={12}
            />
            {club.membersAmount ?? 0} {t('common:enums.MEMBERS')}
          </MetaItem>
        </Meta>
      </Body>
      <ChevronWrapper>
        <MenuRowChevron />
      </ChevronWrapper>
    </ClubCardWrapper>
  );
};
